import { useCallback, useState } from 'react';

/** Three stuck columns already fill a laptop; a fourth would leave nothing to scroll. */
export const MAX_PINS = 3;

const STORAGE_PREFIX = 'nr.compare.pins.';

function read(jobId: string): readonly string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + jobId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string').slice(0, MAX_PINS) : [];
  } catch {
    // Storage can be blocked (a private window); pins then last for the visit only.
    return [];
  }
}

function write(jobId: string, pins: readonly string[]): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + jobId, JSON.stringify(pins));
  } catch {
    // Blocked storage only costs remembering them next time — the screen works the same.
  }
}

/**
 * DEC-02's pins: a viewer's own, per job, kept in this browser (a convenience, never
 * shared — S22 kickoff). The oldest pin makes way when a fourth is added.
 */
export function usePins(jobId: string): { readonly pins: readonly string[]; readonly toggle: (applicationId: string) => void } {
  const [pins, setPins] = useState<readonly string[]>(() => read(jobId));
  const toggle = useCallback(
    (applicationId: string) => {
      setPins((current) => {
        const next = current.includes(applicationId) ? current.filter((id) => id !== applicationId) : [...current, applicationId].slice(-MAX_PINS);
        write(jobId, next);
        return next;
      });
    },
    [jobId],
  );
  return { pins, toggle };
}
